import { log, warn, error } from '@/utils/debugLogger';
import httpService from '@Utils/httpService';
import { parseExcelFile, processExcelData, generatePreviewData } from '../utils/clientExcelProcessor';
import largeDataStorage from '../utils/largeDataStorage';
import { API_LIMITS, UI_THRESHOLDS } from '@/constants/apiLimits';

const baseUrl = 'api/tags';

export default {
  /**
   * Analyze Excel file on client-side with large dataset support
   * @param {File} file - Excel file to analyze
   * @param {Function} onProgress - Progress callback
   * @returns {Promise} Analysis results with column suggestions
   */
  async uploadAndAnalyze(file, onProgress) {
    try {
      // Parse Excel file client-side with progress tracking
      const parseResult = await parseExcelFile(file, onProgress);
      
      if (!parseResult.success) {
        throw new Error(parseResult.error);
      }
      
      // Generate a unique session ID for this upload
      const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // For large datasets, use IndexedDB storage
      if (parseResult.data.totalRows > UI_THRESHOLDS.EXCEL_INDEXEDDB_THRESHOLD) {
        if (onProgress) {
          onProgress({ stage: 'storing', progress: 95, message: 'Storing data for processing...' });
        }

        // Store in IndexedDB instead of sessionStorage
        await largeDataStorage.storeExcelData(uploadId, parseResult.data);

        if (onProgress) {
          onProgress({ stage: 'complete', progress: 100, message: 'Large dataset stored successfully!' });
        }
      } else {
        // For smaller datasets, still use sessionStorage for backward compatibility
        const sessionData = {
          uploadId,
          fileName: file.name,
          headers: parseResult.data.headers,
          dataRows: parseResult.data.dataRows,
          analysis: parseResult.data
        };
        
        try {
          sessionStorage.setItem(`excel_upload_${uploadId}`, JSON.stringify(sessionData));
        } catch (storageError) {
          // If sessionStorage fails, fall back to IndexedDB
          warn('SessionStorage failed, using IndexedDB fallback:', storageError);
          await largeDataStorage.storeExcelData(uploadId, parseResult.data);
        }
      }
      
      // Return analysis in the same format as before
      return {
        data: {
          uploadId,
          fileName: file.name,
          detectedColumns: parseResult.data.detectedColumns,
          sampleData: parseResult.data.sampleData,
          facilitiesDetected: parseResult.data.facilitiesDetected,
          facilityColumn: parseResult.data.facilityColumn,
          totalRows: parseResult.data.totalRows,
          dataQuality: parseResult.data.dataQuality,
          isLargeDataset: parseResult.data.isLargeDataset,
          performanceNote: parseResult.data.performanceNote
        }
      };
    } catch (error) {
      throw new Error(`Excel analysis failed: ${error.message}`);
    }
  },

  /**
   * Process data with column mappings and submit to server
   * @param {string} uploadId - Upload session ID
   * @param {string} assetId - Asset ID
   * @param {Object} mappingData - Column mapping configuration
   * @param {Function} onProgress - Progress callback
   * @returns {Promise} Processing results
   */
  async submitMapping(uploadId, assetId, mappingData, onProgress) {
    try {
      let sessionData;
      let isLargeDataset = false;
      
      // Try to retrieve from sessionStorage first
      const sessionDataStr = sessionStorage.getItem(`excel_upload_${uploadId}`);
      if (sessionDataStr) {
        sessionData = JSON.parse(sessionDataStr);
      } else {
        // Try IndexedDB for large datasets
        const storedData = await largeDataStorage.getExcelData(uploadId);
        if (!storedData) {
          throw new Error('Upload session not found. Please upload the file again.');
        }
        
        sessionData = {
          fileName: storedData.fileName,
          headers: storedData.headers
        };

        isLargeDataset = storedData.totalRows > UI_THRESHOLDS.EXCEL_INDEXEDDB_THRESHOLD;
        
        // For large datasets, process in chunks
        if (isLargeDataset) {
          return await this.processLargeDatasetSubmission(
            uploadId, 
            assetId, 
            mappingData, 
            storedData, 
            onProgress
          );
        } else {
          // Load all data for smaller datasets
          sessionData.dataRows = await storedData.loadAllData();
        }
      }
      
      // Process data client-side
      const processedResult = await processExcelData(
        sessionData.headers,
        sessionData.dataRows,
        mappingData.mappings,
        {
          facilityColumn: mappingData.facilityColumn,
          tagType: mappingData.type,
          onProgress
        }
      );
      
      if (onProgress) {
        onProgress({ 
          stage: 'submitting', 
          progress: 90, 
          message: 'Submitting processed data...' 
        });
      }
      
      // Submit processed data to server (much simpler endpoint)
      const response = await httpService.post({
        url: `${baseUrl}/importProcessedData/${assetId}`,
        data: {
          type: mappingData.type,
          records: processedResult.data.records,
          metadata: {
            ...processedResult.data.metadata,
            originalFileName: sessionData.fileName,
            mappings: mappingData.mappings,
            facilityColumn: mappingData.facilityColumn
          },
          summary: processedResult.data.summary
        }
      });
      
      if (onProgress) {
        onProgress({ 
          stage: 'complete', 
          progress: 100, 
          message: 'Data processing complete!' 
        });
      }
      
      // Clean up session data
      if (sessionDataStr) {
        sessionStorage.removeItem(`excel_upload_${uploadId}`);
      } else {
        await largeDataStorage.removeUpload(uploadId);
      }
      
      return response;
    } catch (error) {
      throw new Error(`Data processing failed: ${error.message}`);
    }
  },

  /**
   * Process large dataset submission with chunked processing
   */
  async processLargeDatasetSubmission(uploadId, assetId, mappingData, storedData, onProgress) {
    try {
      const allRecords = [];
      
      // Process data in chunks to avoid memory issues
      await largeDataStorage.processDataInChunks(
        uploadId,
        async (chunkData, chunkIndex, chunkInfo) => {
          // Process this chunk
          const chunkResult = await processExcelData(
            chunkInfo.headers,
            chunkData,
            mappingData.mappings,
            {
              facilityColumn: mappingData.facilityColumn,
              tagType: mappingData.type
            }
          );
          
          if (chunkResult.success) {
            allRecords.push(...chunkResult.data.records);
          }
          
          return chunkResult.data.records;
        },
        (progress) => {
          if (onProgress) {
            onProgress({
              stage: 'processing',
              progress: Math.round((progress.percentage / 100) * 80), // 80% for processing
              message: `Processing chunk ${progress.completed}/${progress.total}...`,
              processedRows: progress.processedRows
            });
          }
        }
      );
      
      if (onProgress) {
        onProgress({ 
          stage: 'submitting', 
          progress: 90, 
          message: 'Submitting processed data...' 
        });
      }
      
      // Submit all processed records
      const response = await httpService.post({
        url: `${baseUrl}/importProcessedData/${assetId}`,
        data: {
          type: mappingData.type,
          records: allRecords,
          metadata: {
            tagType: mappingData.type,
            facilityColumn: mappingData.facilityColumn,
            originalFileName: storedData.fileName,
            mappings: mappingData.mappings,
            processedAt: new Date().toISOString(),
            processedInChunks: true
          },
          summary: {
            totalRowsProcessed: storedData.totalRows,
            recordsCreated: allRecords.length,
            skippedRows: storedData.totalRows - allRecords.length,
            facilityFiltered: !!mappingData.facilityColumn,
            mappingsApplied: Object.keys(mappingData.mappings).length,
            processedInChunks: true
          }
        }
      });
      
      if (onProgress) {
        onProgress({ 
          stage: 'complete', 
          progress: 100, 
          message: 'Large dataset processing complete!' 
        });
      }
      
      // Clean up
      await largeDataStorage.removeUpload(uploadId);
      
      return response;
    } catch (error) {
      throw new Error(`Large dataset processing failed: ${error.message}`);
    }
  },



  /**
   * Get preview of mapped data (client-side processing)
   * @param {string} uploadId - Upload session ID
   * @param {string} assetId - Asset ID
   * @param {Object} mappings - Current mappings
   * @param {number} limit - Number of rows to preview (default: 1000 for Vuetify table)
   * @returns {Promise} Preview data
   */
  async getPreviewData(uploadId, assetId, mappings, limit = 1000, options = {}) {
    try {
      let sessionData;
      
      // Try sessionStorage first
      const sessionDataStr = sessionStorage.getItem(`excel_upload_${uploadId}`);
      if (sessionDataStr) {
        sessionData = JSON.parse(sessionDataStr);
      } else {
        // Try IndexedDB for large datasets
        const storedData = await largeDataStorage.getExcelData(uploadId);
        if (!storedData) {
          throw new Error('Upload session not found. Please upload the file again.');
        }
        
        // For large datasets, load multiple chunks for better preview experience
        let previewRows;
        if (storedData.totalChunks && storedData.totalChunks > 1) {
          // Calculate how many chunks we need to load for the desired limit
          const rowsPerChunk = Math.floor(storedData.totalRows / storedData.totalChunks);
          const chunksNeeded = Math.min(
            Math.ceil(limit / rowsPerChunk),
            storedData.totalChunks,
            5 // Don't load more than 5 chunks for preview
          );
          
          previewRows = [];
          for (let i = 0; i < chunksNeeded; i++) {
            const chunkData = await storedData.loadChunk(i);
            previewRows.push(...chunkData);
            
            // Stop if we have enough rows
            if (previewRows.length >= limit) {
              previewRows = previewRows.slice(0, limit);
              break;
            }
          }
        } else {
          previewRows = await storedData.loadAllData();
          previewRows = previewRows.slice(0, limit);
        }
        
        sessionData = {
          headers: storedData.headers,
          dataRows: previewRows
        };
      }
      
      // Generate preview data client-side
      const originalRowCount = sessionData.dataRows.length;
      const previewData = generatePreviewData(
        sessionData.headers,
        sessionData.dataRows.slice(0, limit), // Ensure we don't exceed limit
        mappings,
        limit,
        options
      );
      
      // Calculate how many rows were filtered out due to missing Tag No.
      const tagNoColumn = Object.keys(mappings).find(col => mappings[col] === 'name');
      let filteredOutCount = 0;
      
      if (tagNoColumn) {
        const tagNoColumnIndex = sessionData.headers.indexOf(tagNoColumn);
        filteredOutCount = sessionData.dataRows.slice(0, limit).filter(row => {
          const tagNoValue = row[tagNoColumnIndex];
          return !tagNoValue || String(tagNoValue).trim() === '';
        }).length;
      }
      
      return {
        data: previewData,
        metadata: {
          originalRowCount,
          validRowCount: previewData.length,
          filteredOutCount,
          hasTagNoMapping: !!tagNoColumn
        }
      };
    } catch (error) {
      throw new Error(`Preview generation failed: ${error.message}`);
    }
  },

  /**
   * Submit pre-processed data instances directly to server
   * @param {string} assetId - Asset ID
   * @param {Object} processedData - Already processed data with instances
   * @returns {Promise} Submission result
   */
  async submitProcessedData(assetId, processedData) {
    try {
      const response = await httpService.post({
        url: `${baseUrl}/${processedData.type}/importProcessedData/${assetId}`,
        data: processedData.records
      });

      return response;
    } catch (error) {
      throw new Error(`Failed to submit processed data: ${error.message}`);
    }
  },

};
